/**
 * Local player movement prediction (visual only; the sim stays the authority).
 *
 * Other units are drawn interpolated between the previous and the latest tick, i.e. up to one
 * tick in the past. For the player's own unit that lag reads as sluggish WASD, so it is drawn
 * at the *present* instead: the latest sim position extrapolated along the currently held move
 * direction by the time since that tick (negative right after an input-triggered early tick,
 * when the sim is ahead of real time). Pressing a key moves the unit on the very next frame;
 * releasing it stops it.
 *
 * Where the prediction and the sim disagree (walls, roots, knockbacks, a key released mid-tick)
 * the error is blended out over ~TAU ms instead of snapping. Big jumps (respawn, teleport) snap.
 */
import type { Vec2 } from '@keystone/sim';
import { TICK_MS, backpedalFactor } from '@keystone/sim';

export interface LocalMotionInput {
  /** Sim position at the latest tick. */
  simPos: Vec2;
  /** Sim position at the tick before (null after a respawn / first frame). */
  prevPos: Vec2 | null;
  simFacing: number;
  /** Latest sim tick number. */
  tick: number;
  /** Camera-relative move direction from the keys held right now (unit vector or zero). */
  heldDir: Vec2;
  /** The sim's own direction-mode move intent (null if not moving by keys). */
  simDir: Vec2 | null;
  /** Effective move speed in yards/s (0 while rooted / unable to move). */
  speed: number;
  /** The sim moves the unit on its own (knockback, pathing): extrapolate its tick velocity. */
  autoMove: boolean;
  /** Present minus latest tick time, in sim ms (may be negative after an early tick). */
  aheadMs: number;
  /** Cap on |aheadMs| (default one tick). A party run draws the own hero a round trip ahead. */
  maxAheadMs?: number;
  /** Real frame time. */
  dtMs: number;
  /** The drawn path's own velocity (yards/s) when the caller knows it (party prediction); overrides held keys / tick velocity. */
  vel?: Vec2;
  alive: boolean;
  /**
   * WoW controls (input/controls.ts): the facing the hero has at the present (client-steered, else
   * the sim's). Drawn as is instead of the held direction, and moving against it predicts the
   * sim's backpedal speed. Absent: the camera-relative scheme (face where you run).
   */
  aimFacing?: number;
}

export interface LocalMotionOutput {
  pos: Vec2;
  facing: number;
  /** Shown ground speed (yards/s) for the run animation. */
  speed: number;
  /** Shown ground velocity (yards/s): the gait (run / strafe / backpedal) reads its direction. */
  vel: Vec2;
}

/** Error blend time constant (ms). */
export const TAU_MS = 60;
/** Errors larger than this snap (respawn, teleport, charge). */
export const SNAP_YARDS = 4;
/** The sim moving us at under this fraction of the predicted speed means "blocked": use its actual velocity. */
const BLOCKED_FRACTION = 0.6;

const same = (a: Vec2 | null, b: Vec2): boolean => !!a && Math.abs(a.x - b.x) < 1e-3 && Math.abs(a.y - b.y) < 1e-3;

export class LocalMotion {
  private shown: Vec2 | null = null;
  private lastTick = -1;
  /** Consecutive ticks the sim has been moving us in the held direction. */
  private steadyTicks = 0;
  /** Sim velocity over the last tick (yards/s). */
  private tickVel: Vec2 = { x: 0, y: 0 };

  reset(): void {
    this.shown = null;
    this.lastTick = -1;
    this.steadyTicks = 0;
    this.tickVel = { x: 0, y: 0 };
  }

  update(i: LocalMotionInput): LocalMotionOutput {
    if (!i.alive) {
      this.reset();
      return { pos: { x: i.simPos.x, y: i.simPos.y }, facing: i.simFacing, speed: 0, vel: { x: 0, y: 0 } };
    }
    if (i.tick !== this.lastTick) {
      this.lastTick = i.tick;
      this.tickVel = i.prevPos ? { x: ((i.simPos.x - i.prevPos.x) * 1000) / TICK_MS, y: ((i.simPos.y - i.prevPos.y) * 1000) / TICK_MS } : { x: 0, y: 0 };
      this.steadyTicks = same(i.simDir, i.heldDir) && (i.heldDir.x !== 0 || i.heldDir.y !== 0) ? this.steadyTicks + 1 : 0;
    }
    const held = i.heldDir.x !== 0 || i.heldDir.y !== 0;
    let vel: Vec2 = { x: 0, y: 0 };
    if (held && i.speed > 0) {
      // WoW controls: moving against the facing backpedals (the sim's rule, spatial.moveUnit).
      const speed = i.aimFacing === undefined ? i.speed : i.speed * backpedalFactor('human', i.heldDir, i.aimFacing);
      vel = { x: i.heldDir.x * speed, y: i.heldDir.y * speed };
      // Blocked by a wall (or slowed in a way speed doesn't show): trust what the sim did.
      if (this.steadyTicks >= 2 && Math.hypot(this.tickVel.x, this.tickVel.y) < BLOCKED_FRACTION * speed) vel = { ...this.tickVel };
    } else if (i.autoMove) vel = { ...this.tickVel };
    if (i.vel) vel = { ...i.vel };

    const cap = i.maxAheadMs ?? TICK_MS;
    const ahead = Math.max(-TICK_MS, Math.min(cap, i.aheadMs)) / 1000;
    const raw = { x: i.simPos.x + vel.x * ahead, y: i.simPos.y + vel.y * ahead };
    let pos = raw;
    const prev = this.shown;
    if (prev) {
      const dt = Math.max(0, i.dtMs) / 1000;
      // Where we would be if nothing had changed: last shown + this frame's motion.
      const ex = prev.x + vel.x * dt - raw.x;
      const ey = prev.y + vel.y * dt - raw.y;
      if (Math.hypot(ex, ey) <= SNAP_YARDS) {
        const k = Math.exp(-Math.max(0, i.dtMs) / TAU_MS);
        pos = { x: raw.x + ex * k, y: raw.y + ey * k };
      }
    }
    const shownVel = prev && i.dtMs > 0 ? { x: ((pos.x - prev.x) * 1000) / i.dtMs, y: ((pos.y - prev.y) * 1000) / i.dtMs } : { x: vel.x, y: vel.y };
    const speed = Math.hypot(shownVel.x, shownVel.y);
    this.shown = pos;
    const facing = i.aimFacing !== undefined ? i.aimFacing : held ? Math.atan2(i.heldDir.y, i.heldDir.x) : i.simFacing;
    return { pos, facing, speed, vel: shownVel };
  }
}
